<template>
  <div class="sci-view-container">
    <!-- Header -->
    <header class="sci-header">
      <div class="sci-header-left">
        <button class="sci-btn-back" @click="goBack">
          <i class="fas fa-arrow-left"></i>
          <span>{{ isStaff ? 'Gestione Asporto' : ($t('home') || 'Home') }}</span>
        </button>
        <div class="sci-title-group">
          <h1 class="sci-title">
            <i class="fas fa-shopping-bag me-2 text-primary"></i>
            {{ isStaff ? 'Inserimento Ordine Asporto (Staff)' : ($t('client_asporto') || 'Ordina per Asporto') }}
          </h1>
          <p class="sci-subtitle">
            {{ showMenu ? 'Seleziona le portate desiderate e completa l\'ordine' : 'Inserisci i dati del cliente e l\'orario di ritiro desiderato' }}
          </p>
        </div>
      </div>

      <!-- Header Right: Steps & Cart -->
      <div class="sci-header-right">
        <!-- Floating Cart Trigger Button -->
        <button v-if="showMenu" class="sci-cart-pill-btn" @click="showCart = !showCart">
          <i class="fas fa-shopping-cart cart-icon"></i>
          <span class="cart-label">{{ $t('carrello') || 'Carrello' }}</span>
          <span class="cart-badge-count">{{ ordine.length }}</span>
          <span class="cart-badge-total font-monospace">{{ totale.toFixed(2) }} €</span>
        </button>
      </div>
    </header>

    <!-- Step Progress Indicator -->
    <div class="sci-step-bar">
      <div class="step-item" :class="{ active: !showMenu, completed: showMenu }" @click="showMenu ? showMenu = false : null">
        <span class="step-num">1</span>
        <span class="step-text">Dati Cliente & Ritiro</span>
      </div>
      <div class="step-line" :class="{ active: showMenu }"></div>
      <div class="step-item" :class="{ active: showMenu }">
        <span class="step-num">2</span>
        <span class="step-text">Selezione Menu & Piatti</span>
      </div>
    </div>

    <!-- MAIN BODY -->
    <main class="sci-main-content">
      <!-- STEP 1: Customer Details Form -->
      <section v-if="!showMenu" class="customer-step-section">
        <div class="sci-card customer-card">
          <div class="sci-card-header">
            <h2 class="sci-card-title">
              <i class="fas fa-user-edit me-2 text-primary"></i>
              {{ $t('nuovo_asporto_dati') || 'Dati per il Ritiro Asporto' }}
            </h2>
            <span class="sci-caption">Compila i campi sottostanti per verificare la disponibilità delle fasce orarie</span>
          </div>

          <div class="customer-form-body">
            <!-- Customer Name -->
            <div class="sci-form-group">
              <label class="sci-label" for="cust-name">
                <span>{{ $t('nome') || 'Nome e Cognome' }} *</span>
              </label>
              <div class="sci-input-icon-group">
                <i class="fas fa-user input-icon"></i>
                <input 
                  type="text" 
                  id="cust-name" 
                  v-model="customerInfo.nome" 
                  placeholder="Es. Mario Rossi"
                  class="sci-input"
                  required
                />
              </div>
            </div>

            <!-- Phone -->
            <div class="sci-form-group">
              <label class="sci-label" for="cust-phone">
                <span>{{ $t('telefono') || 'Numero di Telefono' }} *</span>
                <span class="sci-label-hint">Per notifiche SMS o chiamata</span>
              </label>
              <div class="sci-input-icon-group">
                <i class="fas fa-phone input-icon"></i>
                <input 
                  type="tel" 
                  id="cust-phone" 
                  v-model="customerInfo.telefono" 
                  placeholder="Es. 333 1234567"
                  class="sci-input"
                  required
                />
              </div>
            </div>

            <!-- Email -->
            <div class="sci-form-group">
              <label class="sci-label" for="cust-email">
                <span>Email {{ isStaff ? '(Opzionale)' : '*' }}</span>
                <span class="sci-label-hint">Ricevuta e conferma d'ordine</span>
              </label>
              <div class="sci-input-icon-group">
                <i class="fas fa-envelope input-icon"></i>
                <input 
                  type="email" 
                  id="cust-email" 
                  v-model="customerInfo.email" 
                  placeholder="mario.rossi@email.it"
                  class="sci-input"
                  :required="!isStaff"
                />
              </div>
            </div>

            <!-- Pickup Date & Time Grid -->
            <div class="pickup-datetime-grid">
              <!-- Date -->
              <div class="sci-form-group">
                <label class="sci-label" for="cust-date">
                  <span>{{ $t('data') || 'Data' }} {{ $t('ritiro') || 'Ritiro' }} *</span>
                </label>
                <div class="sci-input-icon-group">
                  <i class="fas fa-calendar-alt input-icon"></i>
                  <input 
                    type="date" 
                    id="cust-date" 
                    v-model="customerInfo.dataRitiro" 
                    :min="minDate"
                    class="sci-input"
                    required
                  />
                </div>
              </div>

              <!-- Time -->
              <div class="sci-form-group">
                <label class="sci-label" for="cust-time">
                  <span>{{ $t('ora') || 'Ora' }} {{ $t('ritiro') || 'Ritiro' }} *</span>
                </label>
                <div class="sci-input-icon-group">
                  <i class="fas fa-clock input-icon"></i>
                  <input 
                    type="time" 
                    id="cust-time" 
                    v-model="customerInfo.oraRitiro" 
                    :disabled="!isDayAsportoActive"
                    class="sci-input"
                    required
                  />
                </div>
              </div>
            </div>

            <!-- Business Hours / Availability Alert -->
            <div class="time-range-info-card" v-if="customerInfo.dataRitiro">
              <div v-if="!isDayAsportoActive" class="alert-closed">
                <i class="fas fa-ban text-danger me-2"></i>
                <span>L'asporto è <strong>chiuso</strong> il {{ getGiornoName(dayOfWeekOfSelectedDate) }}. Seleziona un'altra data.</span>
              </div>
              <div v-else-if="currentDayAsportoSettings && hasTimeRangesForSelectedDate" class="alert-hours">
                <div class="hours-header">
                  <i class="fas fa-info-circle text-primary me-2"></i>
                  <span>Fasce orarie consentite per <strong>{{ getGiornoName(dayOfWeekOfSelectedDate) }}</strong>:</span>
                </div>
                <div class="hours-badges-row">
                  <span v-if="currentDayAsportoSettings.asportoPranzoStart" class="sci-badge-slot">
                    <i class="fas fa-sun text-warning me-1"></i>
                    Pranzo: {{ currentDayAsportoSettings.asportoPranzoStart }} - {{ currentDayAsportoSettings.asportoPranzoEnd }}
                  </span>
                  <span v-if="currentDayAsportoSettings.asportoCenaStart" class="sci-badge-slot">
                    <i class="fas fa-moon text-primary me-1"></i>
                    Cena: {{ currentDayAsportoSettings.asportoCenaStart }} - {{ currentDayAsportoSettings.asportoCenaEnd }}
                  </span>
                </div>
              </div>
            </div>

            <div v-if="customerInfo.oraRitiro && !isOraRitiroValida" class="alert-time-warning">
              <i class="fas fa-exclamation-triangle me-2"></i>
              <span>L'orario selezionato non ricade nelle fasce consentite di pranzo o cena per questa giornata.</span>
            </div>

            <!-- Next Step Action -->
            <button 
              class="sci-btn sci-btn-primary w-100 btn-proceed-menu" 
              @click="proceedToMenu" 
              :disabled="!isFormValid"
            >
              <span>{{ $t('conferma') || 'Conferma e Scegli i Piatti' }}</span>
              <i class="fas fa-arrow-right ms-2"></i>
            </button>
          </div>
        </div>
      </section>

      <!-- STEP 2: Menu & Dish Selection -->
      <section v-if="showMenu" class="menu-selection-section">
        <!-- Sticky Sub-toolbar: Category filter pills + Search -->
        <div class="sci-toolbar-card menu-filter-card">
          <div class="category-pills-bar">
            <button 
              v-for="cat in availableCategories" 
              :key="cat.id"
              class="cat-pill"
              :class="{ active: selectedCategory === cat.id }"
              @click="selectedCategory = cat.id"
            >
              {{ cat.label }}
            </button>
          </div>

          <div class="menu-search-wrap">
            <i class="fas fa-search search-icon"></i>
            <input 
              type="text" 
              v-model="dishSearchQuery" 
              placeholder="Cerca piatto nel menu..." 
              class="sci-search-input"
            />
            <button v-if="dishSearchQuery" class="clear-btn" @click="dishSearchQuery = ''">&times;</button>
          </div>
        </div>

        <!-- Categories and Dishes Grid -->
        <div class="menu-categories-wrap">
          <div 
            class="category-group-block" 
            v-for="(dishes, categoryId) in filteredPiattiPerPortata" 
            :key="categoryId"
          >
            <div class="category-block-header">
              <h3 class="category-block-title">
                <i class="fas fa-utensils me-2 text-primary"></i>
                {{ formatCategoryLabel(categoryId) }}
              </h3>
              <span class="category-count">{{ dishes.length }} piatti</span>
            </div>

            <div class="dishes-bento-grid">
              <div 
                class="sci-card dish-card" 
                v-for="dish in dishes" 
                :key="dish.nome"
              >
                <div class="dish-card-body">
                  <div class="dish-header-row">
                    <h4 class="dish-name">{{ getTranslatedName(dish) }}</h4>
                    <span class="dish-price font-monospace">{{ dish.prezzo }} €</span>
                  </div>

                  <!-- Description -->
                  <div class="dish-desc-wrap" :class="{ expanded: dish.expanded }">
                    <p class="dish-desc">{{ getTranslatedDesc(dish) }}</p>
                    <div v-if="getAllergeni(dish)" class="dish-allergen-info">
                      <i class="fas fa-shield-alt text-warning me-1"></i>
                      <strong>{{ $t('allergeni') || 'Allergeni' }}:</strong> {{ getAllergeni(dish) }}
                    </div>
                  </div>

                  <!-- Details Accordion Trigger -->
                  <button 
                    v-if="dish.descrizione || getAllergeni(dish)" 
                    class="btn-toggle-desc" 
                    @click="toggleDetails(dish)"
                  >
                    <i class="fas" :class="dish.expanded ? 'fa-chevron-up' : 'fa-info-circle'"></i>
                    <span>{{ dish.expanded ? 'Meno info' : 'Dettagli & Allergeni' }}</span>
                  </button>

                  <!-- Note input for kitchen -->
                  <div class="dish-kitchen-note-wrap">
                    <input 
                      type="text" 
                      v-model="dish.nota" 
                      class="sci-input-sm" 
                      :placeholder="$t('nota', 'Note per la cucina...')"
                    />
                  </div>
                </div>

                <!-- Add Button -->
                <div class="dish-card-footer">
                  <button class="sci-btn sci-btn-primary w-100 btn-add-dish" @click="addToCart(dish)">
                    <i class="fas fa-plus me-1"></i>
                    <span>{{ $t('aggiungi') || 'Aggiungi' }}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>

    <!-- VARIATIONS MODAL -->
    <div class="modal-overlay" v-if="showVariationModal" @click.self="showVariationModal = false">
      <div class="sci-card modal-variations-card">
        <div class="modal-header">
          <div class="modal-title-group">
            <h3 class="modal-title">
              <i class="fas fa-sliders-h me-2 text-primary"></i>
              Personalizza {{ currentDish.nome }}
            </h3>
            <span class="sci-caption">Seleziona aggiunte o variazioni per questo piatto</span>
          </div>
          <button class="btn-close-modal" @click="showVariationModal = false">&times;</button>
        </div>

        <div class="modal-body">
          <!-- Aggiunte -->
          <div class="variation-group" v-if="currentDish.addProd && currentDish.addProd.length">
            <h4 class="variation-title text-success">
              <i class="fas fa-plus-circle me-1"></i> {{ $t('aggiunte') || 'Aggiunte & Supplementi' }}
            </h4>
            <div class="variation-chips-grid">
              <label 
                v-for="add in sortedAddProd" 
                :key="add.prodottoId || add" 
                class="variation-chip"
                :class="{ active: selectedAggiunte.includes(add) }"
              >
                <input type="checkbox" :value="add" v-model="selectedAggiunte" class="sci-checkbox">
                <span>{{ add.descrizione || add.nome || add }}</span>
              </label>
            </div>
          </div>

          <!-- Senza -->
          <div class="variation-group" v-if="currentDish.removeProd && currentDish.removeProd.length">
            <h4 class="variation-title text-danger">
              <i class="fas fa-minus-circle me-1"></i> {{ $t('senza') || 'Ingredienti da Rimuovere (Senza)' }}
            </h4>
            <div class="variation-chips-grid">
              <label 
                v-for="rem in sortedRemoveProd" 
                :key="rem" 
                class="variation-chip chip-remove"
                :class="{ active: selectedSenza.includes(rem) }"
              >
                <input type="checkbox" :value="rem" v-model="selectedSenza" class="sci-checkbox">
                <span>{{ rem }}</span>
              </label>
            </div>
          </div>

          <!-- Note -->
          <div class="sci-form-group">
            <label class="sci-label">Note Speciali:</label>
            <textarea 
              v-model="notaModal" 
              class="sci-textarea" 
              rows="2" 
              placeholder="Es. Ben cotto, poco sale..."
            ></textarea>
          </div>
        </div>

        <div class="modal-footer">
          <button class="sci-btn sci-btn-secondary" @click="showVariationModal = false">
            {{ $t('annulla') || 'Annulla' }}
          </button>
          <button class="sci-btn sci-btn-primary" @click="confirmVariations">
            <i class="fas fa-check me-1"></i>
            {{ $t('conferma') || 'Conferma e Aggiungi' }}
          </button>
        </div>
      </div>
    </div>

    <!-- CART MODAL / DRAWER -->
    <div class="modal-overlay" v-if="showCart" @click.self="showCart = false">
      <div class="sci-card cart-modal-card">
        <div class="modal-header">
          <div class="modal-title-group">
            <h3 class="modal-title">
              <i class="fas fa-shopping-cart me-2 text-primary"></i>
              {{ $t('ordinazione') || 'Riepilogo Ordine' }}
            </h3>
            <span class="sci-caption">{{ ordine.length }} articoli nel carrello</span>
          </div>
          <button class="btn-close-modal" @click="showCart = false">&times;</button>
        </div>

        <div class="modal-body cart-modal-body">
          <div v-if="ordine.length === 0" class="cart-empty-state">
            <i class="fas fa-shopping-cart empty-cart-icon"></i>
            <p>{{ $t('carrello_vuoto') || 'Il carrello è vuoto. Aggiungi i piatti dal menu.' }}</p>
          </div>

          <div v-else class="cart-items-list">
            <div class="cart-row-item" v-for="(item, idx) in ordine" :key="idx">
              <div class="cart-item-info">
                <div class="cart-item-top">
                  <span class="cart-item-name">{{ item.piatto }}</span>
                  <span class="cart-item-subtotal font-monospace">{{ (item.prezzo * item.quantita).toFixed(2) }} €</span>
                </div>
                <div class="cart-item-modifications" v-if="item.aggiunta.length || item.senza.length || item.nota">
                  <span v-if="item.aggiunta.length" class="text-success small-mod">
                    + {{ formatAggiunte(item.aggiunta) }}
                  </span>
                  <span v-if="item.senza.length" class="text-danger small-mod">
                    - {{ formatSenza(item.senza) }}
                  </span>
                  <span v-if="item.nota" class="text-muted small-mod">
                    <i>Note: {{ item.nota }}</i>
                  </span>
                </div>
              </div>

              <div class="cart-item-actions">
                <span class="cart-qty font-monospace">x{{ item.quantita }}</span>
                <button class="btn-remove-item" @click="removeItem(idx)" title="Rimuovi">
                  <i class="fas fa-trash-alt"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer cart-footer" v-if="ordine.length > 0">
          <div class="cart-total-row">
            <span class="total-text">{{ $t('totale') || 'Totale Complessivo' }}:</span>
            <span class="total-val font-monospace">{{ totale.toFixed(2) }} €</span>
          </div>

          <!-- Customer Checkout (Stripe) -->
          <div v-if="!isStaff" class="checkout-actions">
            <button class="sci-btn sci-btn-primary w-100 btn-pay-stripe" @click="proceedToPayment" :disabled="isProcessing">
              <i class="fas" :class="isProcessing ? 'fa-spinner fa-spin' : 'fa-credit-card'"></i>
              <span>{{ isProcessing ? 'Elaborazione...' : ($t('paga_stripe') || 'Paga Online con Carta (Stripe)') }}</span>
            </button>
          </div>

          <!-- Staff Checkout -->
          <div v-else class="staff-checkout-actions">
            <button class="sci-btn sci-btn-primary btn-staff-paid" @click="proceedToCreateOrder(true)" :disabled="isProcessing">
              <i class="fas fa-check-circle me-1"></i>
              <span>{{ isProcessing ? '...' : ($t('crea_ordine_pagato') || 'Salva Ordine (Pagato)') }}</span>
            </button>
            <button class="sci-btn sci-btn-secondary btn-staff-pending" @click="proceedToCreateOrder(false)" :disabled="isProcessing">
              <i class="fas fa-hourglass-half me-1"></i>
              <span>{{ isProcessing ? '...' : ($t('crea_ordine_da_pagare') || 'Salva (Da Pagare al Ritiro)') }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Error/Notice Floating Toast -->
    <transition name="fade">
      <div class="sci-floating-toast" v-if="errorMessage">
        <i class="fas fa-exclamation-circle me-2"></i>
        <span>{{ errorMessage }}</span>
      </div>
    </transition>
  </div>
</template>

<script>
import { store } from '../store/store.js'

export default {
  name: 'OrdineAsporto',
  data() {
    return {
      store,
      customerInfo: {
        nome: '',
        email: '',
        telefono: '',
        dataRitiro: '',
        oraRitiro: ''
      },
      showMenu: false,
      showCart: false,
      piatti: [],
      piattiPerPortata: {},
      ordine: [],
      isProcessing: false,
      errorMessage: '',
      showVariationModal: false,
      currentDish: null,
      selectedAggiunte: [],
      selectedSenza: [],
      notaModal: '',
      selectedCategory: 'all',
      dishSearchQuery: '',
      predefinedCategories: [
        { id: 'antipasti', label: 'nostri_antipasti' },
        { id: 'primi', label: 'nostri_primi' },
        { id: 'secondi', label: 'nostri_secondi' },
        { id: 'poke', label: 'nostri_poke' },
        { id: 'contorni', label: 'nostri_contorni' },
        { id: 'pizza', label: 'nostra_pizza' },
        { id: 'sushi', label: 'nostro_sushi' },
        { id: 'dolci', label: 'nostri_dolci' },
        { id: 'bevande', label: 'nostre_bevande' }
      ],
      asportoSettings: null
    }
  },
  computed: {
    availableCategories() {
      const cats = [{ id: 'all', label: this.$t('tutti') || 'Tutti' }];
      this.predefinedCategories.forEach(p => {
        if (this.piattiPerPortata[p.id]) {
          cats.push({ id: p.id, label: this.$t(p.label) || p.id });
        }
      });
      return cats;
    },
    filteredPiattiPerPortata() {
      let base = this.piattiPerPortata;
      if (this.selectedCategory !== 'all') {
        base = {};
        if (this.piattiPerPortata[this.selectedCategory]) {
          base[this.selectedCategory] = this.piattiPerPortata[this.selectedCategory];
        }
      }

      if (!this.dishSearchQuery) return base;

      const q = this.dishSearchQuery.toLowerCase().trim();
      const result = {};
      Object.keys(base).forEach(cat => {
        const matches = base[cat].filter(dish => 
          (dish.nome && dish.nome.toLowerCase().includes(q)) ||
          (dish.descrizione && dish.descrizione.toLowerCase().includes(q))
        );
        if (matches.length) {
          result[cat] = matches;
        }
      });
      return result;
    },
    dayOfWeekOfSelectedDate() {
      if (!this.customerInfo.dataRitiro) return null;
      const parts = this.customerInfo.dataRitiro.split('-');
      if (parts.length !== 3) return null;
      const yyyy = parseInt(parts[0], 10);
      const mm = parseInt(parts[1], 10) - 1;
      const dd = parseInt(parts[2], 10);
      const d = new Date(yyyy, mm, dd);
      return d.getDay();
    },
    currentDayAsportoSettings() {
      if (!this.asportoSettings) return null;
      const dow = this.dayOfWeekOfSelectedDate;
      if (dow !== null && dow !== undefined && this.asportoSettings.giorni) {
        const daySetting = this.asportoSettings.giorni[dow] || this.asportoSettings.giorni[String(dow)];
        if (daySetting) {
          return {
            attivo: daySetting.attivo !== false,
            asportoPranzoStart: daySetting.asportoPranzoStart || this.asportoSettings.asportoPranzoStart,
            asportoPranzoEnd: daySetting.asportoPranzoEnd || this.asportoSettings.asportoPranzoEnd,
            asportoCenaStart: daySetting.asportoCenaStart || this.asportoSettings.asportoCenaStart,
            asportoCenaEnd: daySetting.asportoCenaEnd || this.asportoSettings.asportoCenaEnd
          };
        }
      }
      return {
        attivo: true,
        asportoPranzoStart: this.asportoSettings.asportoPranzoStart,
        asportoPranzoEnd: this.asportoSettings.asportoPranzoEnd,
        asportoCenaStart: this.asportoSettings.asportoCenaStart,
        asportoCenaEnd: this.asportoSettings.asportoCenaEnd
      };
    },
    isDayAsportoActive() {
      const s = this.currentDayAsportoSettings;
      return s ? s.attivo : true;
    },
    hasTimeRangesForSelectedDate() {
      const s = this.currentDayAsportoSettings;
      if (!s || !s.attivo) return false;
      return !!(s.asportoPranzoStart || s.asportoCenaStart);
    },
    isOraRitiroValida() {
      if (!this.customerInfo.dataRitiro || !this.customerInfo.oraRitiro) return true;
      if (this.isStaff) return true;
      const s = this.currentDayAsportoSettings;
      if (!s) return true;
      if (!s.attivo) return false;

      const formatTime = (t) => t ? String(t).padStart(5, '0') : '';
      const ora = formatTime(this.customerInfo.oraRitiro);
      const pStart = formatTime(s.asportoPranzoStart);
      const pEnd = formatTime(s.asportoPranzoEnd);
      const cStart = formatTime(s.asportoCenaStart);
      const cEnd = formatTime(s.asportoCenaEnd);

      const isLunch = pStart && pEnd ? (ora >= pStart && ora <= pEnd) : false;
      const isDinner = cStart && cEnd ? (ora >= cStart && ora <= cEnd) : false;

      if (!pStart && !pEnd && !cStart && !cEnd) return true;
      return isLunch || isDinner;
    },
    isFormValid() {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const phoneRegex = /^[\d\s+\-()]{6,20}$/;
      const emailVal = this.customerInfo.email ? this.customerInfo.email.trim() : '';
      const isEmailValid = this.isStaff
        ? (!emailVal || emailRegex.test(emailVal))
        : (!!emailVal && emailRegex.test(emailVal));

      return !!(this.customerInfo.nome && this.customerInfo.nome.trim() && 
             isEmailValid &&
             this.customerInfo.telefono && this.customerInfo.telefono.trim() &&
             phoneRegex.test(this.customerInfo.telefono.trim()) &&
             this.customerInfo.dataRitiro &&
             this.customerInfo.oraRitiro &&
             this.isDayAsportoActive &&
             this.isOraRitiroValida);
    },
    minDate() {
      return new Date().toISOString().split('T')[0];
    },
    totale() {
      return this.ordine.reduce((sum, item) => sum + (item.prezzo * item.quantita), 0);
    },
    isStaff() {
      return this.$route.query.mode === 'staff';
    },
    sortedAddProd() {
      if (!this.currentDish || !this.currentDish.addProd) return [];
      return [...this.currentDish.addProd].sort((a, b) => {
        const nameA = a.descrizione || a.nome || String(a);
        const nameB = b.descrizione || b.nome || String(b);
        return nameA.localeCompare(nameB, 'it', { sensitivity: 'base', numeric: true });
      });
    },
    sortedRemoveProd() {
      if (!this.currentDish || !this.currentDish.removeProd) return [];
      return [...this.currentDish.removeProd].sort((a, b) => {
        const nameA = a.descrizione || a.nome || String(a);
        const nameB = b.descrizione || b.nome || String(b);
        return nameA.localeCompare(nameB, 'it', { sensitivity: 'base', numeric: true });
      });
    }
  },
  methods: {
    formatCategoryLabel(id) {
      const predefined = this.predefinedCategories.find(p => p.id === id);
      if (predefined) return this.$t(predefined.label) || id;
      const translation = this.$t(id);
      if (translation !== id) return translation;
      return id.charAt(0).toUpperCase() + id.slice(1);
    },
    getTranslatedName(item) {
      const locale = this.$i18n ? this.$i18n.locale : 'it';
      if (locale === 'en') return item.nomeEn || item.nome;
      if (locale === 'es') return item.nomeEs || item.nome;
      return item.nome;
    },
    getTranslatedDesc(item) {
      const locale = this.$i18n ? this.$i18n.locale : 'it';
      if (locale === 'en') return item.descrizioneEn || item.descrizione;
      if (locale === 'es') return item.descrizioneEs || item.descrizione;
      return item.descrizione;
    },
    formatAggiunte(list) {
      if (!list || !list.length) return '';
      const names = list.map(a => a.descrizione || a.nome || String(a)).filter(Boolean);
      return names.join(', ');
    },
    formatSenza(list) {
      if (!list || !list.length) return '';
      const names = list.map(s => s.descrizione || s.nome || String(s)).filter(Boolean);
      return names.join(', ');
    },
    goBack() {
      if (this.showMenu) {
        this.showMenu = false;
      } else {
        if (this.isStaff) {
          this.$router.push({ name: "GestioneAsporto" });
        } else {
          this.$router.push({ name: "Home" });
        }
      }
    },
    proceedToMenu() {
      if (this.isFormValid) {
        this.showMenu = true;
      }
    },
    async displayMenu() {
      try {
        const response = await this.axios.post(this.store.path + 'displayMenu');
        if (response.data && response.data.messaggio) {
          this.piatti = response.data.messaggio.map(element => ({
            ...element,
            expanded: false,
            nota: ''
          }));
          this.groupPiattiByPortata();
        }
      } catch (error) {
        console.error('Error loading menu:', error);
        this.errorMessage = this.$t('products_load_failed') || 'Errore durante il caricamento del menu';
      }
    },
    groupPiattiByPortata() {
      const grouped = {};
      this.piatti.forEach(element => {
        const p = element.portata || 'altro';
        if (!grouped[p]) grouped[p] = [];
        grouped[p].push(element);
      });
      this.piattiPerPortata = grouped;
    },
    addToCart(dish) {
      if ((dish.addProd && dish.addProd.length) || (dish.removeProd && dish.removeProd.length)) {
        this.currentDish = dish;
        this.selectedAggiunte = [];
        this.selectedSenza = [];
        this.notaModal = dish.nota || '';
        this.showVariationModal = true;
      } else {
        this.addOrderItem(dish.nome, dish.prezzo, dish.portata, [], [], dish._id || dish.id, dish.nota || '');
        dish.nota = '';
      }
    },
    confirmVariations() {
      this.addOrderItem(
        this.currentDish.nome, 
        this.currentDish.prezzo, 
        this.currentDish.portata, 
        [...this.selectedAggiunte], 
        [...this.selectedSenza], 
        this.currentDish._id || this.currentDish.id, 
        this.notaModal
      );
      this.currentDish.nota = '';
      this.showVariationModal = false;
    },
    addOrderItem(nome, prezzo, portata, aggiunta, senza, id, nota) {
      const existing = this.ordine.find(item => 
        item.piatto === nome && 
        item.piattoId === id &&
        JSON.stringify(item.aggiunta) === JSON.stringify(aggiunta) && 
        JSON.stringify(item.senza) === JSON.stringify(senza) &&
        item.nota === nota
      );
      
      if (existing) {
        existing.quantita++;
      } else {
        this.ordine.push({
          piatto: nome,
          piattoId: id,
          prezzo: prezzo,
          portata: portata,
          quantita: 1,
          aggiunta: aggiunta,
          senza: senza,
          nota: nota
        });
      }
    },
    removeItem(index) {
      this.ordine.splice(index, 1);
    },
    async proceedToPayment() {
      if (this.ordine.length === 0) {
        this.errorMessage = this.$t('carrello_vuoto') || 'Il carrello è vuoto';
        return;
      }

      this.isProcessing = true;
      this.errorMessage = '';

      try {
        const response = await this.axios.post(this.store.path + 'creaSessionePagamento', {
          nome: this.customerInfo.nome,
          email: this.customerInfo.email,
          telefono: this.customerInfo.telefono,
          dataRitiro: this.customerInfo.dataRitiro,
          oraRitiro: this.customerInfo.oraRitiro,
          ordinazione: this.ordine,
          totale: Math.round(this.totale * 100)
        });

        if (response.data && response.data.sessionUrl) {
          window.location.href = response.data.sessionUrl;
        } else {
          this.errorMessage = 'Errore durante la creazione della sessione Stripe';
        }
      } catch (error) {
        console.error('Payment error:', error);
        this.errorMessage = error.response?.data?.messaggio || 'Errore durante il pagamento';
      } finally {
        this.isProcessing = false;
      }
    },
    async proceedToCreateOrder(alreadyPaid) {
      if (this.ordine.length === 0) {
        this.errorMessage = this.$t('carrello_vuoto') || 'Il carrello è vuoto';
        return;
      }

      this.isProcessing = true;
      this.errorMessage = '';

      try {
        await this.axios.post(this.store.path + 'creaOrdineAsportoStaff', {
          nome: this.customerInfo.nome,
          email: this.customerInfo.email,
          telefono: this.customerInfo.telefono,
          dataRitiro: this.customerInfo.dataRitiro,
          oraRitiro: this.customerInfo.oraRitiro,
          ordinazione: this.ordine,
          totale: Math.round(this.totale * 100),
          pagato: alreadyPaid,
          headers: {
            authorization: `Bearer ${JSON.parse(localStorage.token)}`
          }
        });

        alert('Ordine Asporto creato con successo!');
        this.$router.push({ name: 'GestioneAsporto' });
      } catch (error) {
        console.error('Staff order creation error:', error);
        this.errorMessage = error.response?.data?.messaggio || 'Errore nella creazione ordine';
      } finally {
        this.isProcessing = false;
      }
    },
    getAllergeni(dish) {
      if (!dish) return '';
      const locale = this.$i18n ? this.$i18n.locale : 'it';
      if (locale === 'en') return dish.allergeniEn || dish.allergeni;
      if (locale === 'es') return dish.allergeniEs || dish.allergeni;
      return dish.allergeni;
    },
    toggleDetails(dish) {
      dish.expanded = !dish.expanded;
    },
    getGiornoName(dow) {
      if (dow === null || dow === undefined) return '';
      const names = {
        0: 'Domenica',
        1: 'Lunedì',
        2: 'Martedì',
        3: 'Mercoledì',
        4: 'Giovedì',
        5: 'Venerdì',
        6: 'Sabato'
      };
      return names[dow] || '';
    },
    async fetchSettings() {
      try {
        const response = await this.axios.get(this.store.path + 'prenotazioni-setting');
        const setting = response.data.find(s => s.codicesala === 'asporto');
        if (setting) {
          this.asportoSettings = setting;
        }
      } catch (error) {
        console.error('Error fetching asporto settings:', error);
      }
    }
  },
  beforeMount() {
    this.fetchSettings();
    this.displayMenu();
  }
}
</script>

<style scoped>
.sci-view-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: var(--sci-bg, #f8fafc);
  color: var(--sci-text-main, #0f172a);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  padding-bottom: 60px;
}

/* Header */
.sci-header {
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  padding: 16px 28px;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid var(--sci-border, #e2e8f0);
  box-shadow: 0 4px 20px -2px rgba(0, 0, 0, 0.04);
}

.sci-header-left {
  display: flex;
  align-items: center;
  gap: 20px;
}

.sci-btn-back {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 16px;
  background: #ffffff;
  color: #334155;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
}

.sci-btn-back:hover {
  background: #f1f5f9;
  border-color: #94a3b8;
  color: #0f172a;
  transform: translateX(-2px);
}

.sci-title-group {
  display: flex;
  flex-direction: column;
}

.sci-title {
  margin: 0;
  font-size: 1.35rem;
  font-weight: 700;
  color: #0f172a;
  display: flex;
  align-items: center;
}

.sci-subtitle {
  margin: 2px 0 0 0;
  font-size: 0.85rem;
  color: #64748b;
}

/* Cart Pill in Header */
.sci-cart-pill-btn {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-height: 44px;
  padding: 6px 18px;
  border-radius: 30px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: #ffffff;
  border: none;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(16, 185, 129, 0.35);
  transition: all 0.2s ease;
}

.sci-cart-pill-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 18px rgba(16, 185, 129, 0.45);
}

.cart-icon {
  font-size: 1.1rem;
}

.cart-badge-count {
  background: rgba(0, 0, 0, 0.2);
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.85rem;
}

.cart-badge-total {
  background: #ffffff;
  color: #065f46;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 0.9rem;
}

/* Step Bar Indicator */
.sci-step-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 600px;
  margin: 20px auto 0 auto;
  padding: 0 24px;
  width: 100%;
}

.step-item {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #94a3b8;
  cursor: default;
  transition: all 0.2s;
}

.step-item.completed {
  color: #059669;
  cursor: pointer;
}

.step-item.active {
  color: #0f172a;
  font-weight: 700;
}

.step-num {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.9rem;
  color: #64748b;
}

.step-item.active .step-num {
  background: #2563eb;
  color: #ffffff;
}

.step-item.completed .step-num {
  background: #10b981;
  color: #ffffff;
}

.step-text {
  font-size: 0.92rem;
}

.step-line {
  flex: 1;
  height: 2px;
  background: #e2e8f0;
  margin: 0 16px;
  transition: background 0.3s;
}

.step-line.active {
  background: #10b981;
}

/* Main Content */
.sci-main-content {
  max-width: 1400px;
  margin: 24px auto;
  padding: 0 24px;
  width: 100%;
}

/* Step 1 Customer Form Card */
.customer-step-section {
  display: flex;
  justify-content: center;
}

.customer-card {
  width: 100%;
  max-width: 600px;
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid var(--sci-border, #e2e8f0);
  box-shadow: 0 4px 20px -2px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.sci-card-header {
  padding: 20px 24px;
  border-bottom: 1px solid #f1f5f9;
  background: #fafbfc;
}

.sci-card-title {
  margin: 0 0 4px 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1e293b;
  display: flex;
  align-items: center;
}

.sci-caption {
  font-size: 0.85rem;
  color: #64748b;
}

.customer-form-body {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.sci-form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.sci-label {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-size: 0.9rem;
  font-weight: 600;
  color: #334155;
}

.sci-label-hint {
  font-size: 0.78rem;
  font-weight: normal;
  color: #94a3b8;
}

.sci-input-icon-group {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon {
  position: absolute;
  left: 14px;
  color: #94a3b8;
  font-size: 1rem;
  pointer-events: none;
}

.sci-input {
  width: 100%;
  min-height: 46px;
  padding: 10px 14px 10px 42px;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  font-size: 0.95rem;
  color: #0f172a;
  background: #ffffff;
  transition: all 0.2s ease;
}

.sci-input:focus {
  outline: none;
  border-color: #10b981;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.15);
}

.pickup-datetime-grid {
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 16px;
}

@media (max-width: 500px) {
  .pickup-datetime-grid {
    grid-template-columns: 1fr;
  }
}

/* Time range info card */
.time-range-info-card {
  border-radius: 10px;
  overflow: hidden;
}

.alert-closed {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #b91c1c;
  padding: 12px 16px;
  border-radius: 10px;
  font-size: 0.88rem;
  display: flex;
  align-items: center;
}

.alert-hours {
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  padding: 12px 16px;
  border-radius: 10px;
}

.hours-header {
  font-size: 0.85rem;
  color: #1e40af;
  margin-bottom: 8px;
}

.hours-badges-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.sci-badge-slot {
  background: #ffffff;
  color: #1e293b;
  border: 1px solid #cbd5e1;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 600;
}

.alert-time-warning {
  background: #fffbeb;
  border: 1px solid #fde68a;
  color: #b45309;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 0.85rem;
}

.btn-proceed-menu {
  min-height: 48px;
  font-size: 1rem;
  margin-top: 8px;
}

/* Step 2 Menu Selection */
.menu-filter-card {
  position: sticky;
  top: 76px;
  z-index: 90;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 14px;
  margin-bottom: 24px;
}

.category-pills-bar {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 4px;
  max-width: 75%;
}

.cat-pill {
  min-height: 38px;
  padding: 6px 16px;
  border-radius: 20px;
  border: 1px solid #cbd5e1;
  background: #ffffff;
  color: #475569;
  font-size: 0.88rem;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.2s;
}

.cat-pill:hover {
  background: #f8fafc;
}

.cat-pill.active {
  background: #0f172a;
  color: #ffffff;
  border-color: #0f172a;
}

.menu-search-wrap {
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 12px;
  color: #94a3b8;
  font-size: 0.9rem;
}

.sci-search-input {
  min-height: 40px;
  padding: 6px 30px 6px 34px;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  font-size: 0.9rem;
  background: #ffffff;
  color: #0f172a;
}

.clear-btn {
  position: absolute;
  right: 10px;
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
}

/* Category Groups */
.category-group-block {
  margin-bottom: 32px;
}

.category-block-header {
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 16px;
  border-bottom: 2px solid #e2e8f0;
  padding-bottom: 8px;
}

.category-block-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: #0f172a;
}

.category-count {
  font-size: 0.82rem;
  color: #94a3b8;
  font-weight: 600;
}

/* Dishes Bento Grid */
.dishes-bento-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px;
}

.dish-card {
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid var(--sci-border, #e2e8f0);
  box-shadow: 0 4px 16px -2px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  transition: transform 0.2s, box-shadow 0.2s;
}

.dish-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px -4px rgba(0, 0, 0, 0.08);
}

.dish-card-body {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.dish-header-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;
}

.dish-name {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: #0f172a;
}

.dish-price {
  font-size: 1.15rem;
  font-weight: 800;
  color: #059669;
  white-space: nowrap;
}

.dish-desc-wrap {
  max-height: 48px;
  overflow: hidden;
  transition: max-height 0.3s ease-out;
}

.dish-desc-wrap.expanded {
  max-height: 250px;
}

.dish-desc {
  margin: 0;
  font-size: 0.85rem;
  color: #64748b;
  line-height: 1.4;
}

.dish-allergen-info {
  margin-top: 8px;
  padding-top: 6px;
  border-top: 1px dotted #e2e8f0;
  font-size: 0.8rem;
  color: #b45309;
}

.btn-toggle-desc {
  background: none;
  border: none;
  color: #2563eb;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.sci-input-sm {
  width: 100%;
  min-height: 36px;
  padding: 4px 10px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 0.85rem;
  background: #f8fafc;
}

.dish-card-footer {
  padding: 14px 20px;
  border-top: 1px solid #f1f5f9;
  background: #fafbfc;
}

.btn-add-dish {
  min-height: 42px;
}

/* Modals */
.modal-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-variations-card, .cart-modal-card {
  width: 100%;
  max-width: 540px;
  background: #ffffff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0,0,0,0.2);
  display: flex;
  flex-direction: column;
  max-height: 90vh;
}

.modal-header {
  padding: 20px 24px;
  border-bottom: 1px solid #f1f5f9;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-title {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 700;
  color: #0f172a;
}

.btn-close-modal {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #94a3b8;
  cursor: pointer;
}

.modal-body {
  padding: 24px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.variation-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.variation-title {
  margin: 0;
  font-size: 0.92rem;
  font-weight: 700;
}

.variation-chips-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

@media (max-width: 500px) {
  .variation-chips-grid {
    grid-template-columns: 1fr;
  }
}

.variation-chip {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 44px;
  padding: 8px 12px;
  border-radius: 10px;
  background: #f8fafc;
  border: 1px solid #cbd5e1;
  cursor: pointer;
  font-size: 0.88rem;
  transition: all 0.2s;
}

.variation-chip:hover {
  background: #f1f5f9;
}

.variation-chip.active {
  background: #ecfdf5;
  border-color: #10b981;
  color: #065f46;
  font-weight: 600;
}

.variation-chip.chip-remove.active {
  background: #fef2f2;
  border-color: #ef4444;
  color: #991b1b;
}

.sci-checkbox {
  width: 18px;
  height: 18px;
  accent-color: #10b981;
}

.sci-textarea {
  width: 100%;
  padding: 10px;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  font-size: 0.9rem;
}

.modal-footer {
  padding: 16px 24px;
  background: #fafbfc;
  border-top: 1px solid #f1f5f9;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

/* Cart Items List */
.cart-items-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.cart-row-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  gap: 12px;
}

.cart-item-info {
  display: flex;
  flex-direction: column;
  flex: 1;
}

.cart-item-top {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
}

.cart-item-name {
  font-weight: 700;
  color: #0f172a;
  font-size: 0.98rem;
}

.cart-item-subtotal {
  font-weight: 700;
  color: #059669;
}

.cart-item-modifications {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 4px;
}

.small-mod {
  font-size: 0.8rem;
}

.cart-item-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.cart-qty {
  font-weight: 700;
  color: #2563eb;
  background: #eff6ff;
  padding: 4px 8px;
  border-radius: 6px;
}

.btn-remove-item {
  background: none;
  border: 1px solid #fecaca;
  color: #ef4444;
  border-radius: 6px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-remove-item:hover {
  background: #fee2e2;
  color: #b91c1c;
}

.cart-footer {
  flex-direction: column;
  gap: 14px;
}

.cart-total-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  width: 100%;
}

.total-text {
  font-size: 1rem;
  font-weight: 700;
  color: #334155;
}

.total-val {
  font-size: 1.4rem;
  font-weight: 800;
  color: #0f172a;
}

.checkout-actions, .staff-checkout-actions {
  width: 100%;
  display: flex;
  gap: 10px;
}

.btn-pay-stripe {
  min-height: 48px;
  font-size: 1.05rem;
}

.btn-staff-paid {
  flex: 1;
}

.btn-staff-pending {
  flex: 1;
}

/* Empty cart state */
.cart-empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #94a3b8;
}

.empty-cart-icon {
  font-size: 3rem;
  margin-bottom: 12px;
  opacity: 0.4;
}

/* Floating Toast */
.sci-floating-toast {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  background: #0f172a;
  color: #ffffff;
  padding: 12px 24px;
  border-radius: 30px;
  box-shadow: 0 10px 25px rgba(0,0,0,0.25);
  font-weight: 600;
  font-size: 0.9rem;
  z-index: 1100;
}

/* Generic sci-btn */
.sci-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 18px;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
}

.sci-btn-primary {
  background: #10b981;
  color: #ffffff;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.25);
}

.sci-btn-primary:hover:not(:disabled) {
  background: #059669;
  transform: translateY(-1px);
}

.sci-btn-secondary {
  background: #ffffff;
  color: #334155;
  border: 1px solid #cbd5e1;
}

.sci-btn-secondary:hover:not(:disabled) {
  background: #f8fafc;
  border-color: #94a3b8;
}

.sci-btn:disabled {
  background: #cbd5e1;
  color: #94a3b8;
  cursor: not-allowed;
  box-shadow: none;
}
</style>
